import { firebaseAuth } from "@/firebase/auth";

interface UserAttributes {
  first_name: string;
  last_name: string;
}

interface CreateUserRequest {
  data: {
    type: "users";
    attributes: UserAttributes;
  };
}

interface UserResponse {
  data: {
    id: string;
    type: "users";
    attributes: {
      email: string;
      first_name: string;
      last_name: string;
      role: string;
    };
  };
  meta?: {
    token_refresh_required?: boolean;
  };
}

interface TigimClaim {
  role: "user" | "admin" | (string & {});
  user_id: string;
}

export class UserService {
  private async makeAuthenticatedRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
    let token: string;

    try {
      if (await firebaseAuth.isTokenExpiringSoon(5)) {
        console.log("Token expiring soon, refreshing...");
        token = await firebaseAuth.getIdToken(true);
      } else {
        token = await firebaseAuth.getIdToken(false);
      }
    } catch (error) {
      console.error("Failed to get authentication token:", error);
      throw new Error("Authentication failed. Please sign in again.");
    }

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/vnd.api+json",
      Accept: "application/vnd.api+json",
      ...options.headers
    };

    const response = await fetch(url, {
      ...options,
      headers
    });

    if (!response.ok) {
      let errorMessage = `Request failed: ${response.status} ${response.statusText}`;

      try {
        const errorData = await response.json();

        if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          errorMessage = errorData.errors[0].detail || errorMessage;
        }
      } catch {
        // If we can't parse error response, use the generic message
      }

      throw new Error(errorMessage);
    }

    if (response.status === 204 || response.headers.get("content-length") === "0") {
      return undefined as T;
    }

    try {
      return await response.json();
    } catch (error) {
      console.error("Failed to parse response JSON:", error);
      throw new Error("Invalid response from server");
    }
  }

  async createUser(userAttributes: UserAttributes): Promise<UserResponse> {
    const baseUrl = firebaseAuth.getApiBaseUrl();
    const url = `${baseUrl}/api/admin/users`;

    const requestBody: CreateUserRequest = {
      data: {
        type: "users",
        attributes: userAttributes
      }
    };

    try {
      const response = await this.makeAuthenticatedRequest<UserResponse>(url, {
        method: "POST",
        body: JSON.stringify(requestBody)
      });

      if (response.meta?.token_refresh_required) {
        console.log("Token refresh required, refreshing token...");

        try {
          await firebaseAuth.getIdToken(true);

          const tokenResult = await firebaseAuth.getIdTokenResult();

          if (!tokenResult.claims.tigim) {
            throw new Error("Backend account not found. Please contact support.");
          }
        } catch (error) {
          console.error("Token refresh failed:", error);
          throw new Error("Failed to refresh authentication token");
        }
      }

      return response;
    } catch (error) {
      console.error("User creation failed:", error);
      throw error;
    }
  }

  async deleteUser(): Promise<void> {
    try {
      const tokenResult = await firebaseAuth.getIdTokenResult();
      const tigimClaim = tokenResult.claims.tigim as TigimClaim | undefined;

      if (!tigimClaim?.user_id) {
        throw new Error("Backend account not found. Please contact support.");
      }

      const userId = tigimClaim.user_id;
      const baseUrl = firebaseAuth.getApiBaseUrl();
      const url = `${baseUrl}/api/admin/users/${userId}`;

      await this.makeAuthenticatedRequest<void>(url, {
        method: "DELETE"
      });
    } catch (error) {
      console.error("User deletion failed:", error);
      throw error;
    }
  }

  extractUserNameFromAuth(): UserAttributes | null {
    const user = firebaseAuth.getCurrentUser();

    if (!user) {
      return null;
    }

    if (user.displayName) {
      const nameParts = user.displayName.trim().split(" ");

      if (nameParts.length >= 2) {
        return {
          first_name: nameParts[0],
          last_name: nameParts.slice(1).join(" ")
        };
      } else if (nameParts.length === 1) {
        return {
          first_name: nameParts[0],
          last_name: ""
        };
      }
    }

    const email = user.email;

    if (email) {
      const emailPrefix = email.split("@")[0];

      return {
        first_name: emailPrefix,
        last_name: ""
      };
    }

    return {
      first_name: "User",
      last_name: ""
    };
  }
}

export const userService = new UserService();
